/**
 * threadChatView.component.tsx
 * Sentriqs
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Sentriqs, Inc. All rights reserved.
 *
**/
import bluebird from "bluebird";
import Picker from "@emoji-mart/react";
import moment from "moment-timezone";
import * as React from "react";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";

import CloseIcon from "@mui/icons-material/Close";
import { Alert, CircularProgress, ClickAwayListener, IconButton, Popper } from "@mui/material";

import { AuthContext } from "../auth/auth.context";
import { GroupNavContext } from "../chat/groupNav.context";

import { ErrorSnackBar } from "../components/errorSnackBar.component";
import { ThreadMessageListView } from "./threadMessageListView.component"; 

import { PermissionLevel } from "../models/auth";
import { ContentMessage, DisplayMessage } from "../models/message";
import { CustomEmoji, EmojiSelection, emojiSelectionToContent } from "../models/reaction";

import { dbServiceFactory } from "../services/db.service";
import { groupServiceFactory } from "../services/group.service";
import { workerServiceFactory } from "../services/worker.service";



export function ThreadChatViewWrapper(): React.ReactElement<any, any> {
  const { threadID } = useParams();
  const groupNavContext = React.useContext<GroupNavContext>(GroupNavContext);
  return <ThreadChatViewComponent threadID={threadID} groupNavContext={groupNavContext} />;
}

interface ThreadChatViewProps {
  threadID: string | undefined
  groupNavContext: GroupNavContext
}
//let threadEditingDraftTimeout: NodeJS.Timeout | null = null;

interface State {
  loading: boolean,
  mainMessage: ContentMessage | null,
  replies: Array<DisplayMessage>,
  canSendMessages: boolean,
  openEmojiPicker: boolean,
  anchorEl: HTMLElement | null,
  reactingMessage: ContentMessage | null,
  errMsg: string
}

class ThreadChatViewComponent extends React.Component<ThreadChatViewProps, State> {
  static contextType = AuthContext;
  declare context: React.ContextType<typeof AuthContext>;
  
  private threadID: string;
  private viewTimeout: NodeJS.Timeout | null;

  constructor(props: ThreadChatViewProps) {
    super(props);
    this.state = {
      loading: true,
      mainMessage: null,
      replies: [],
      canSendMessages: false,
      openEmojiPicker: false,
      anchorEl: null,
      reactingMessage: null,
      errMsg: ""
    };
    this.threadID = props.threadID || "";
    this.viewTimeout = null;
    this.onNewMessage = this.onNewMessage.bind(this);
    this.onRemoveMessage = this.onRemoveMessage.bind(this);
    this.onReaction = this.onReaction.bind(this);
    this.onRemoveReaction = this.onRemoveReaction.bind(this);
    this.doMarkThreadAsRead = this.doMarkThreadAsRead.bind(this);
    this.showEmojiPicker = this.showEmojiPicker.bind(this);
    this.choseReaction = this.choseReaction.bind(this);
    
  }

  componentDidMount() {
    const workerService = workerServiceFactory();
    workerService.on("message", this.onNewMessage);
    workerService.on("delmsg", this.onRemoveMessage);
    workerService.on("reaction", this.onReaction);
    workerService.on("remReaction", this.onRemoveReaction);

    const canSendMessages = this.props.groupNavContext.currentGroup &&
      this.props.groupNavContext.can(PermissionLevel.WRITE, "messages");

    this.loadThread().then(() => {
      this.setState({ canSendMessages: canSendMessages || false });
      this.viewTimeout && clearTimeout(this.viewTimeout);
      this.viewTimeout = setTimeout(() => {
        this.doMarkThreadAsRead();
      }, 2500);
    });
    
  }

  componentDidUpdate(prevProps: ThreadChatViewProps) {
    if (prevProps.threadID !== this.props.threadID) {
      this.threadID = this.props.threadID || "";
      this.loadThread();
    }
    if (prevProps.groupNavContext.currentGroup !== this.props.groupNavContext.currentGroup) {
      this.setState({ canSendMessages: this.props.groupNavContext.can(PermissionLevel.WRITE, "messages") || false });
    }
  }
  private loadThread(): Promise<void> {
    if (!this.threadID) { return bluebird.resolve(); }
    this.setState({ loading: true });
    return groupServiceFactory().getThread(this.threadID)
      .then(thread => {
        this.setState({ mainMessage: thread.main as ContentMessage, replies: thread.replies });
      }).then(null, err => {
        console.error("Error loading message:", err);
      }).then(() => this.setState({ loading: false }));
  }

  componentWillUnmount() {
    const workerService = workerServiceFactory();
    workerService.off("message", this.onNewMessage);
    workerService.off("delmsg", this.onRemoveMessage);
    workerService.off("reaction", this.onReaction);
    workerService.off("remReaction", this.onRemoveReaction);
  }

  async onNewMessage(msgID: string, parent: string | undefined) {
    if (!parent || parent !== this.threadID ) { return; }
    const msg = await groupServiceFactory().getReply(msgID);
    if (!msg || !msg._id) {
      return;
    }
    if (msg.threadID !== this.threadID) {
      return;
    }
    const creation = DateTime.isDateTime(msg.created) ? msg.created :
      moment.isMoment(msg.created) ? DateTime.fromJSDate(msg.created.toDate()) :
      DateTime.fromJSDate(msg.created);
    msg.created = creation;
    this.setState(state => ({replies: state.replies.concat(msg)}));
  }

  async onRemoveMessage(groupID: string, msgID: string) {
    if (!this.state.mainMessage || this.state.mainMessage.groupID !== groupID) { return; }
    if (this.state.mainMessage._id === msgID) {
      this.setState({mainMessage: null});
      this.props.groupNavContext.toggleRightDetail();
      return;
    }
    const index = this.state.replies.findIndex(reply => reply._id === msgID);
    if (index < 0) { return; }
    const newArr = Array.from(this.state.replies);
    newArr.splice(index, 1);
    this.setState({replies: newArr});
  }

  async onReaction(msgID: string, reactionID: string, userID: string,
      contentID: string) {
    
    const message = (this.state.mainMessage?._id == msgID ? this.state.mainMessage :
      this.state.replies.find(reply => reply._id === msgID)) as ContentMessage;
    if (!message) { return; }

    try {
      const reaction = await dbServiceFactory().getReaction(reactionID);
      if (!reaction) { return; }

      const reactionUser = {
        reactionID: reactionID,
        userID: userID,
        firstname: reaction.firstname,
        lastname: reaction.lastname
      };

      if (!message.reactions) { message.reactions = {}; }
      if (!message.reactions[contentID]) {
        if (!reaction.users) { reaction.users = []; }
        reaction.users.push(reactionUser);
        message.reactions[contentID] = reaction;
      } else {
        const reaction = message.reactions[contentID];
        if (!reaction.users) { reaction.users = []; }
        reaction.users.push(reactionUser);
      }

      if (this.state.mainMessage?._id === msgID) {
        this.setState({mainMessage: Object.assign({}, this.state.mainMessage)});
      } else {
        this.setState({replies: Array.from(this.state.replies)});
      }
    } catch (err) {
      console.error("Error adding reaction:", err);
      this.setState({errMsg: (err as Error).message});
    }
  }

  async onRemoveReaction(msgID: string, contentID: string, userID: string) {
    const message = (this.state.mainMessage?._id == msgID ? this.state.mainMessage :
      this.state.replies.find(reply => reply._id === msgID)) as ContentMessage;
    if (!message) { return; }

    if (!message.reactions || !message.reactions[contentID]) { return; }
    const reaction = message.reactions[contentID];
    if (!reaction.users) { return; }

    const index = reaction.users.findIndex(u => u.userID === userID);
    if (index < 0) { return; }

    reaction.users.splice(index, 1);
    if (reaction.users.length <= 0) {
      delete message.reactions[contentID];
    }

    if (this.state.mainMessage?._id === msgID) {
      this.setState({mainMessage: Object.assign({}, this.state.mainMessage)});
    } else {
      this.setState({replies: Array.from(this.state.replies)});
    }
  }

  async doMarkThreadAsRead() {
    if (!this.state.mainMessage || !this.props.groupNavContext.currentGroup) { return; }
    try {
      const accountID = this.props.groupNavContext.currentGroup.accountID;
      const userID = this.props.groupNavContext.currentGroup.userID;
      await groupServiceFactory().markThreadAsRead(accountID, userID,
        this.state.mainMessage.groupID, this.threadID);
    } catch (err) {
      console.error("Error marking thread as read:", err);
      this.setState({errMsg: (err as Error).message});
    }
  }

  showEmojiPicker (anchor: HTMLElement, msg: ContentMessage) {
    this.setState({anchorEl: anchor, reactingMessage: msg, openEmojiPicker: true});
  }
  async choseReaction (emoji: EmojiSelection) {
    const {mainMessage, reactingMessage} = this.state;
    if (!mainMessage || !reactingMessage || !this.context.membership) { return; }

    if (!reactingMessage.reactions) {
      reactingMessage.reactions = {};
    }
    const reactions = reactingMessage.reactions;
    const existing = reactions[emoji.id];

    if (existing && existing.users &&
        existing.users.findIndex(u => u.userID === this.context.membership?.userID) >= 0) {
      this.setState({openEmojiPicker: false, reactingMessage: null, anchorEl: null});
      return;
    }

    const { content, contentType } = emojiSelectionToContent(emoji);
    const {accountID, firstname, lastname} = this.context.membership;
    
    try {
      const tID = reactingMessage._id === mainMessage?._id ? "" : reactingMessage.threadID;
      await groupServiceFactory().sendReaction(accountID,
        reactingMessage.groupID, reactingMessage._id, tID,
        emoji.id, contentType, content,
        firstname, lastname);

      this.setState({mainMessage: Object.assign({}, mainMessage)});
    } catch (err) {
      console.error("Error adding reaction:", err);
      this.setState({errMsg: (err as Error).message});
    }
    this.setState({openEmojiPicker: false, reactingMessage: null, anchorEl: null});
  }

  render() {
    return <>
      <header className="group-header-nav">
      <IconButton className="toggle-right-btn" size="small" disableRipple disableTouchRipple
          onClick={this.props.groupNavContext.toggleRightDetail}>
        <CloseIcon />
      </IconButton>
        <h3>Thread</h3>
      </header>
      <div className="right-side-container">
        {this.state.loading ? <div className="group-loading"><CircularProgress /></div> :
        this.state.mainMessage ? <ThreadMessageListView
          threadID={this.threadID}
          canSendMessages={this.state.canSendMessages}
          mainMessage={this.state.mainMessage}
          replies={this.state.replies}
          reactingMessage={this.state.reactingMessage}
          getMatchingMembers={this.props.groupNavContext.getMatchingMembers}
          showEmojiPicker={this.showEmojiPicker}
          onFocus={this.doMarkThreadAsRead}
          didExpandList={this.doMarkThreadAsRead}
          onError={msg => this.setState({errMsg: msg})}/> :
        <div><Alert severity="error">Thread Not Found</Alert></div>
        }
      </div>
      <Popper open={this.state.openEmojiPicker} anchorEl={this.state.anchorEl}
          disablePortal placement="bottom-end">
        <ClickAwayListener onClickAway={() => {
            this.setState({openEmojiPicker: false, anchorEl: null, reactingMessage: null});
            }}>
          <div>
            <Picker custom={CustomEmoji} onEmojiSelect={this.choseReaction} />
          </div>
        </ClickAwayListener>
      </Popper>
      <ErrorSnackBar errMsg={this.state.errMsg} didClose={() => this.setState({errMsg: ""})} />
    </>;
  }
}
